import { AppError } from "./errors";

/** 校验公网 HTTP/HTTPS 回调地址；HTTP 兼容非 TLS 端点，但不放宽 SSRF 安全边界。 */
export function publicCallbackUrlIssue(value: string): string | null {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return "Callback URL is invalid";
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return "Callback URL must use HTTP or HTTPS";
  }
  if (url.username.length > 0 || url.password.length > 0 || url.hash.length > 0) {
    return "Callback credentials and fragments are forbidden";
  }
  const hostname = url.hostname.toLowerCase().replace(/\.$/u, "");
  if (
    hostname === "localhost" || hostname.endsWith(".localhost") ||
    hostname.endsWith(".local") || hostname.endsWith(".internal") ||
    hostname.endsWith(".home.arpa") || isNonPublicIpAddress(hostname)
  ) {
    return "Callback URL must use a public hostname";
  }
  return null;
}

/** Revalidates persisted callback configuration immediately before egress. */
export function requirePublicCallbackUrl(value: string): URL {
  const issue = publicCallbackUrlIssue(value);
  if (issue !== null) throw new AppError("INVALID_ARGUMENT", issue, 400);
  return new URL(value);
}

/** Returns true for private, loopback, link-local, multicast and reserved IPs. */
export function isNonPublicIpAddress(hostname: string): boolean {
  const unwrapped = hostname.startsWith("[") && hostname.endsWith("]")
    ? hostname.slice(1, -1)
    : hostname;
  if (unwrapped.includes(":")) return isNonPublicIpv6(unwrapped);
  const octets = unwrapped.split(".").map(Number);
  if (
    octets.length !== 4 ||
    octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)
  ) {
    return false;
  }
  const [first = -1, second = -1, third = -1] = octets;
  return first === 0 || first === 10 || first === 127 || first >= 224 ||
    (first === 100 && second >= 64 && second <= 127) ||
    (first === 169 && second === 254) ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 0 && third === 0) ||
    (first === 192 && second === 0 && third === 2) ||
    (first === 192 && second === 168) ||
    (first === 198 && (second === 18 || second === 19)) ||
    (first === 198 && second === 51 && third === 100) ||
    (first === 203 && second === 0 && third === 113);
}

function isNonPublicIpv6(value: string): boolean {
  const hextets = expandIpv6(value);
  if (hextets === null) return true;
  const [first = 0, second = 0] = hextets;
  const allZero = hextets.every((hextet) => hextet === 0);
  const loopback = hextets.slice(0, 7).every((hextet) => hextet === 0) && hextets[7] === 1;
  const mappedIpv4 = hextets.slice(0, 5).every((hextet) => hextet === 0) && hextets[5] === 0xffff;
  if (mappedIpv4) {
    const high = hextets[6] ?? 0;
    const low = hextets[7] ?? 0;
    return isNonPublicIpAddress(
      `${high >> 8}.${high & 0xff}.${low >> 8}.${low & 0xff}`,
    );
  }
  return allZero || loopback ||
    (first & 0xfe00) === 0xfc00 || // Unique-local fc00::/7.
    (first & 0xffc0) === 0xfe80 || // Link-local fe80::/10.
    (first & 0xffc0) === 0xfec0 || // Deprecated site-local fec0::/10.
    (first & 0xff00) === 0xff00 || // Multicast ff00::/8.
    (first === 0x2001 && second === 0x0db8); // Documentation 2001:db8::/32.
}

function expandIpv6(value: string): number[] | null {
  const halves = value.split("::");
  if (halves.length > 2) return null;
  const left = parseHextets(halves[0] ?? "");
  const right = parseHextets(halves[1] ?? "");
  if (left === null || right === null) return null;
  if (halves.length === 1) return left.length === 8 ? left : null;
  const omitted = 8 - left.length - right.length;
  return omitted >= 1 ? [...left, ...Array<number>(omitted).fill(0), ...right] : null;
}

function parseHextets(value: string): number[] | null {
  if (value === "") return [];
  const parts = value.split(":");
  if (parts.some((part) => !/^[0-9a-f]{1,4}$/u.test(part))) return null;
  return parts.map((part) => Number.parseInt(part, 16));
}
